import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, AlertTriangle, Wrench, CheckCircle2, RotateCcw, Package, Upload, FileText, BookOpen } from "lucide-react";
import { toast } from "sonner";

/**
 * Onderhoudspagina: eenmalige/periodieke onderhoudsacties die alleen admins
 * mogen doen. Momenteel: herstel van historische dubbele voorraadafboekingen
 * per verkoopkans. Bedoeld om na een deploy op productie 1x te draaien.
 */
export default function InstellingenOnderhoud() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);
  const [bfPreview, setBfPreview] = useState(null);
  const [bfApplied, setBfApplied] = useState(null);
  const [hlStatus, setHlStatus] = useState(null);
  const [hlBusy, setHlBusy] = useState(false);

  useEffect(() => {
    api.get("/onderhoud/handleiding/status").then((r) => setHlStatus(r.data)).catch(() => {});
  }, []);

  const verversHandleiding = async () => {
    setHlBusy(true);
    try {
      const { data } = await api.get("/onderhoud/handleiding/status");
      setHlStatus(data);
      toast.success(`Bijgewerkt: ${data.endpoints} endpoints, ${data.collecties} collecties`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verversen mislukt");
    } finally { setHlBusy(false); }
  };

  const downloadHandleiding = async () => {
    setHlBusy(true);
    try {
      const res = await api.get("/onderhoud/handleiding.md", { responseType: "blob" });
      const naam = hlStatus?.bestandsnaam || "crm-handleiding.md";
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/markdown;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = naam;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${naam} gedownload`);
      verversHandleiding();
    } catch (e) {
      toast.error("Download mislukt");
    } finally { setHlBusy(false); }
  };

  const doDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/producten/herstel-dubbele-afboekingen", { dry_run: true });
      setPreview(data);
      setApplied(null);
      if (!data.duplicaten_gevonden) toast.success("Geen dubbele afboekingen gevonden — alles klopt al.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview mislukt");
    }
    setBusy(false);
  };

  const doApply = async () => {
    if (!preview?.duplicaten_gevonden) return;
    if (!window.confirm(
      `${preview.duplicaten_gevonden} duplicaat-mutaties gaan definitief verwijderd worden.\n\n` +
      `Netto voorraad-effect: ${preview.totaal_voorraad_effect_stuks > 0 ? "+" : ""}${preview.totaal_voorraad_effect_stuks} stuks.\n` +
      `De verwijderde mutaties worden in een backup bewaard zodat je de actie ongedaan kunt maken.\n\n` +
      `Doorgaan?`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/producten/herstel-dubbele-afboekingen", { dry_run: false });
      setApplied(data);
      toast.success(`${data.duplicaten_gevonden} duplicaten verwijderd (audit-id: ${data.audit_id})`);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toepassen mislukt");
    }
    setBusy(false);
  };

  const doUndo = async () => {
    if (!applied?.audit_id) return;
    if (!window.confirm(`Alle correcties met audit-id ${applied.audit_id} verwijderen en voorraad terugdraaien?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/producten/herstel-dubbele-afboekingen/undo?audit_id=${encodeURIComponent(applied.audit_id)}`);
      toast.success(`${data.verwijderd} correcties teruggedraaid`);
      setApplied(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo mislukt");
    }
    setBusy(false);
  };

  const bfDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/producten/backfill-moeder-afboek", { dry_run: true });
      setBfPreview(data); setBfApplied(null);
      if (data.nieuwe_mutaties === 0) toast.success("Geen historische child-verkopen zonder moeder-afboek — alles klopt al.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview mislukt"); }
    setBusy(false);
  };

  const bfApply = async () => {
    if (!bfPreview || bfPreview.nieuwe_mutaties === 0) return;
    const totaal = Object.values(bfPreview.details || []).reduce((s, e) => {
      return s + Object.values(e.voorraad_delta || {}).reduce((a, b) => a + b, 0);
    }, 0);
    if (!window.confirm(
      `Er worden ${bfPreview.nieuwe_mutaties} moeder-afboek mutaties aangemaakt op ${bfPreview.moeders_geraakt} moeder-product(en).\n\n` +
      `Netto voorraad-effect: ${totaal} stuks (moeder-voorraad wordt verlaagd).\n\n` +
      `Doorgaan?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/producten/backfill-moeder-afboek", { dry_run: false });
      setBfApplied(data); setBfPreview(null);
      toast.success(`${data.nieuwe_mutaties} moeder-afboek mutaties aangemaakt op ${data.moeders_geraakt} product(en)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Toepassen mislukt"); }
    setBusy(false);
  };

  const [inboxBusy, setInboxBusy] = useState(false);
  const [inboxResult, setInboxResult] = useState(null);
  const [fwBusy, setFwBusy] = useState(false);
  const [fwMailbox, setFwMailbox] = useState("");
  const [fwResult, setFwResult] = useState(null);

  const forwardTest = async () => {
    if (!fwMailbox.trim()) { toast.error("Vul een mailbox-adres in"); return; }
    setFwBusy(true); setFwResult(null);
    try {
      const { data } = await api.post(`/inbox/forward-test?mailbox=${encodeURIComponent(fwMailbox.trim())}`);
      setFwResult(data);
      if (data.ok) toast.success(`Forward-test verstuurd — check ${data.forward_naar}`);
      else toast.error(data.reason || "Test mislukt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test mislukt"); }
    setFwBusy(false);
  };

  const bfInboxDryRun = async () => {
    setInboxBusy(true);
    try {
      const { data } = await api.post("/inbox/backfill-verzonden?dry_run=true");
      setInboxResult({ ...data, mode: "dry" });
      const totaal = (data.facturen||0) + (data.creditfacturen||0) + (data.inkooporders||0) + (data.pakbonnen||0);
      if (totaal === 0) toast.success("Geen historische mails om terug te vullen — alles al gelogd.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview mislukt"); }
    setInboxBusy(false);
  };

  const bfInboxApply = async () => {
    if (!window.confirm("Ontbrekende verzonden-berichten worden aan de inbox toegevoegd (dubbele records worden overgeslagen). Doorgaan?")) return;
    setInboxBusy(true);
    try {
      const { data } = await api.post("/inbox/backfill-verzonden?dry_run=false");
      setInboxResult({ ...data, mode: "applied" });
      const totaal = (data.facturen||0) + (data.creditfacturen||0) + (data.inkooporders||0) + (data.pakbonnen||0);
      toast.success(`${totaal} historische verzonden-berichten toegevoegd aan de inbox`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Toepassen mislukt"); }
    setInboxBusy(false);
  };

  // Klantcode-import via CSV upload
  const [kcFile, setKcFile] = useState(null);
  const [kcBusy, setKcBusy] = useState(false);
  const [kcResult, setKcResult] = useState(null);

  const kcRun = async (dryRun) => {
    if (!kcFile) { toast.error("Kies eerst een CSV-bestand"); return; }
    setKcBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", kcFile);
      const { data } = await api.post(
        `/organisaties/klantcode-import-csv?dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setKcResult({ ...data, mode: dryRun ? "dry" : "applied" });
      if (dryRun) {
        toast.success(`Preview: ${data.gezet_via_id + data.gezet_via_fallback} klantcodes te zetten`);
      } else {
        toast.success(`${data.gezet_via_id + data.gezet_via_fallback} klantcodes gezet`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import mislukt");
    }
    setKcBusy(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="onderhoud-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Terug
        </Button>
      </div>

      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Onderhoud</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Eenmalige of periodieke acties voor admins. Elke actie heeft een dry-run mode; toepassen kan alleen na expliciete bevestiging.
        </p>
      </div>

      {/* Card: technische handleiding downloaden */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="onderhoud-handleiding">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-medium">Technische handleiding (voor een AI of ontwikkelaar)</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Eén markdown-bestand met alles wat iemand moet weten om over dit CRM mee te denken zonder
              bestaande werking te verbouwen: modules en functionaliteiten, architectuur, datamodel per
              collectie, de rekenregels die vastliggen (prognose, historie, omloopsnelheid, prijzen, BTW),
              de integraties, de achtergrondtaken en de valkuilen. De lijst met API-endpoints, collecties,
              pagina's, geplande taken en de laatste wijzigingen worden bij elke download <strong>opnieuw uit
              de live applicatie opgebouwd</strong> — nieuwe functionaliteit staat er dus automatisch in.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={downloadHandleiding} disabled={hlBusy} data-testid="handleiding-download-btn">
            <FileText className="h-4 w-4 mr-2" />
            {hlBusy ? "Bezig…" : "Download handleiding (.md)"}
          </Button>
          <Button variant="outline" onClick={verversHandleiding} disabled={hlBusy}
            data-testid="handleiding-verversen-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Inhoud verversen
          </Button>
        </div>

        {hlStatus && (
          <div className="text-xs text-muted-foreground border-t border-border pt-3" data-testid="handleiding-status">
            Laatst opgebouwd: {new Date(hlStatus.gegenereerd_op).toLocaleString("nl-NL")} ·{" "}
            <strong className="text-foreground">{hlStatus.endpoints}</strong> API-endpoints ·{" "}
            <strong className="text-foreground">{hlStatus.collecties}</strong> collecties ·{" "}
            {hlStatus.routers} routers · {hlStatus.paginas} pagina's ·{" "}
            {hlStatus.regels.toLocaleString("nl-NL")} regels (± {hlStatus.geschatte_paginas} pagina's)
          </div>
        )}
      </div>

      {/* Card: klantcode CSV-import */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="onderhoud-kc">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Upload className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-medium">Klantcodes importeren (CSV)</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload de PerfectView-export <code className="font-mono text-[11px] bg-secondary px-1 rounded">alle organisaties.csv</code>. De kolom <strong>Relatiecode</strong> wordt overgenomen als <strong>klantcode</strong> per organisatie, gematcht op de PerfectView <code className="font-mono text-[11px] bg-secondary px-1 rounded">Id (relatie)</code>. Fallback via naam + postcode. Bestaande klantcodes worden overschreven — idempotent.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">CSV-bestand</label>
            <Input
              type="file" accept=".csv" data-testid="kc-file"
              onChange={(e) => { setKcFile(e.target.files?.[0] || null); setKcResult(null); }}
              className="w-72"
            />
          </div>
          <Button
            variant="outline" onClick={() => kcRun(true)} disabled={kcBusy || !kcFile}
            data-testid="kc-dry-run"
          >
            <FileText className="h-4 w-4 mr-1" /> {kcBusy && kcResult?.mode !== "applied" ? "Bezig…" : "Preview (dry-run)"}
          </Button>
          {kcResult && kcResult.mode === "dry" && (kcResult.gezet_via_id + kcResult.gezet_via_fallback) > 0 && (
            <Button
              onClick={() => kcRun(false)} disabled={kcBusy}
              data-testid="kc-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> {kcBusy ? "Bezig…" : `Toepassen (${kcResult.gezet_via_id + kcResult.gezet_via_fallback})`}
            </Button>
          )}
        </div>

        {kcResult && (
          <div className="text-sm border-t border-border pt-3 mt-1 space-y-1" data-testid="kc-result">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {kcResult.mode === "applied" ? "Toegepast" : "Preview"} — {kcResult.bestandsnaam}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-1">
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px]">CSV-rijen</div>
                <div className="tabular-nums font-medium mt-0.5">{kcResult.csv_rijen}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px]">Met Relatiecode</div>
                <div className="tabular-nums font-medium mt-0.5">{kcResult.kandidaten_met_relatiecode}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-emerald-50 border-emerald-200">
                <div className="text-emerald-800 uppercase text-[10px]">{kcResult.mode === "applied" ? "Gezet via Id" : "Zou zetten via Id"}</div>
                <div className="tabular-nums font-medium mt-0.5 text-emerald-900">{kcResult.gezet_via_id}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-sky-50 border-sky-200">
                <div className="text-sky-800 uppercase text-[10px]">Fallback naam+pc</div>
                <div className="tabular-nums font-medium mt-0.5 text-sky-900">{kcResult.gezet_via_fallback}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground pt-1">
              Niet gevonden via Id: <strong>{kcResult.niet_gevonden_via_id}</strong> · Totaal klanten in DB: <strong>{kcResult.totaal_klanten_in_db}</strong> · Met klantcode: <strong>{kcResult.met_klantcode_in_db}</strong> ({Math.round(kcResult.met_klantcode_in_db / Math.max(kcResult.totaal_klanten_in_db, 1) * 100)}%)
            </div>
          </div>
        )}
      </div>

      {/* Card: dubbele voorraadafboekingen */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Wrench className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-medium">Herstel dubbele voorraadafboekingen</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Detecteert verkoopkansen die per ongeluk meerdere keren voorraad hebben afgeboekt (via meerdere pakbonnen). De <strong>laatst aangemaakte</strong> afboeking blijft leidend; oudere duplicaten worden <strong>fysiek verwijderd</strong> en de voorraad wordt teruggedraaid. Verwijderde mutaties worden in een backup bewaard voor undo.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={doDryRun} disabled={busy} variant="outline" data-testid="onderhoud-dry-run">
            {busy && !preview ? "Bezig…" : "Zoek duplicaten (dry-run)"}
          </Button>
          {preview?.duplicaten_gevonden > 0 && (
            <Button onClick={doApply} disabled={busy} data-testid="onderhoud-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {busy ? "Bezig…" : `Toepassen (${preview.duplicaten_gevonden})`}
            </Button>
          )}
          {applied?.audit_id && (
            <Button onClick={doUndo} disabled={busy} variant="outline" data-testid="onderhoud-undo"
              className="border-destructive/40 text-destructive hover:bg-destructive/5">
              <RotateCcw className="h-4 w-4 mr-1" /> Ongedaan maken
            </Button>
          )}
        </div>

        {applied?.audit_id && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="onderhoud-applied">
            <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" /> Toegepast</div>
            <div className="mt-1 text-xs">
              {applied.duplicaten_gevonden} duplicaten verwijderd · netto voorraad-effect {applied.totaal_voorraad_effect_stuks > 0 ? "+" : ""}{applied.totaal_voorraad_effect_stuks} stuks · audit-id: <code className="font-mono">{applied.audit_id}</code>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-2">
            <div className={`p-3 rounded-lg border text-sm ${preview.duplicaten_gevonden > 0 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
              <div className="flex items-center gap-2 font-medium">
                {preview.duplicaten_gevonden > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                {preview.duplicaten_gevonden > 0
                  ? `${preview.duplicaten_gevonden} dubbele afboekingen gevonden — netto voorraad-effect ${preview.totaal_voorraad_effect_stuks > 0 ? "+" : ""}${preview.totaal_voorraad_effect_stuks} stuks`
                  : "Geen dubbele afboekingen — alles klopt"}
              </div>
            </div>

            {preview.voorstellen?.length > 0 && (
              <div className="border border-border rounded-lg overflow-hidden max-h-[480px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40 sticky top-0">
                    <tr>
                      <th className="text-left p-2.5">SKU / Product</th>
                      <th className="text-left p-2.5">Kans</th>
                      <th className="text-left p-2.5">Duplicaat pakbon</th>
                      <th className="text-left p-2.5">Leidend blijft</th>
                      <th className="text-right p-2.5">Voorraad Δ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview.voorstellen.map((v, i) => (
                      <tr key={v.mutatie_id || i} data-testid={`onderhoud-voorstel-${i}`}>
                        <td className="p-2.5">
                          <div className="font-mono text-xs">{v.sku || "—"}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[16rem]">{v.product_naam || v.product_id}</div>
                        </td>
                        <td className="p-2.5 font-mono text-xs">{(v.kans_id || "").slice(0, 8)}</td>
                        <td className="p-2.5 font-mono text-xs text-rose-700">{v.referentie}</td>
                        <td className="p-2.5 font-mono text-xs text-emerald-700">{v.leidende_pakbon}</td>
                        <td className={`p-2.5 text-right font-medium ${v.netto_voorraadeffect > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
                          {v.netto_voorraadeffect > 0 ? `+${v.netto_voorraadeffect}` : "0"}
                          {v.eerdere_correctie_id && <span className="ml-1 text-[10px] text-muted-foreground" title="Vorige correctie wordt ook opgeruimd">↺</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card: backfill moeder-afboek voor historische child-verkopen */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="backfill-moeder-card">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-800">
            <Package className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-medium">Backfill moeder-afboek (sticker-model)</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Voor het sticker-model actief werd, boekte een verkoop van een gestickerde variant alleen de child-voorraad af — niet de fysieke moeder. Deze actie zoekt alle historische child-verkopen en schiet met terugwerkende kracht de ontbrekende <strong>moeder-afboek mutaties</strong> in (notitie "Omgestickerd verkocht als …"), en corrigeert de moeder-voorraad met dezelfde hoeveelheid. Idempotent: kan meerdere keren gerund worden.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={bfDryRun} disabled={busy} variant="outline" data-testid="backfill-dry-run">
            {busy && !bfPreview ? "Bezig…" : "Scan (dry-run)"}
          </Button>
          {bfPreview?.nieuwe_mutaties > 0 && (
            <Button onClick={bfApply} disabled={busy} data-testid="backfill-apply"
              className="bg-amber-600 hover:bg-amber-700 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {busy ? "Bezig…" : `Toepassen (${bfPreview.nieuwe_mutaties})`}
            </Button>
          )}
        </div>

        {bfApplied && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="backfill-applied">
            <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" /> Toegepast</div>
            <div className="mt-1 text-xs">
              {bfApplied.nieuwe_mutaties} moeder-afboek mutaties aangemaakt · {bfApplied.moeders_geraakt} moeder-product(en) geraakt · {bfApplied.reeds_aanwezig_overgeslagen} reeds aanwezig overgeslagen
            </div>
          </div>
        )}

        {bfPreview && (
          <div className="space-y-2">
            <div className={`p-3 rounded-lg border text-sm ${bfPreview.nieuwe_mutaties > 0 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
              <div className="flex items-center gap-2 font-medium">
                {bfPreview.nieuwe_mutaties > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                {bfPreview.nieuwe_mutaties > 0
                  ? `${bfPreview.nieuwe_mutaties} ontbrekende moeder-afboek mutaties over ${bfPreview.moeders_geraakt} moeder-product(en) — ${bfPreview.reeds_aanwezig_overgeslagen} reeds aanwezig`
                  : "Geen ontbrekende moeder-afboeken — alles klopt"}
              </div>
            </div>

            {bfPreview.details?.length > 0 && (
              <div className="border border-border rounded-lg overflow-hidden max-h-[480px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40 sticky top-0">
                    <tr>
                      <th className="text-left p-2.5">Moeder</th>
                      <th className="text-right p-2.5">Nieuw</th>
                      <th className="text-right p-2.5">Δ WH</th>
                      <th className="text-right p-2.5">Δ Lev</th>
                      <th className="text-right p-2.5">Δ China</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {bfPreview.details.map((e, i) => (
                      <tr key={e.moeder_id} data-testid={`backfill-voorstel-${i}`}>
                        <td className="p-2.5 font-mono text-xs">{e.moeder_sku}</td>
                        <td className="p-2.5 text-right">{e.nieuw.length}</td>
                        <td className="p-2.5 text-right text-rose-700">{e.voorraad_delta?.warehouse || 0}</td>
                        <td className="p-2.5 text-right text-rose-700">{e.voorraad_delta?.leverancier || 0}</td>
                        <td className="p-2.5 text-right text-rose-700">{e.voorraad_delta?.china || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card: backfill historische verzonden-mails naar inbox */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="backfill-inbox-card">
        <div>
          <h2 className="font-display text-2xl font-medium">Historische verzonden mails in inbox terugvullen</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Verstuurde facturen, creditfacturen, inkooporders (incl. doorzet-mails) en pakbonnen die vóór de auto-logging zijn verzonden, alsnog zichtbaar maken in de Verzonden-tab van de Inbox. Bestaande records worden overgeslagen (idempotent).
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={bfInboxDryRun} disabled={inboxBusy} variant="outline" data-testid="backfill-inbox-dry-run">
            {inboxBusy ? "Bezig…" : "Preview (dry-run)"}
          </Button>
          <Button onClick={bfInboxApply} disabled={inboxBusy} data-testid="backfill-inbox-apply">
            {inboxBusy ? "Bezig…" : "Toepassen"}
          </Button>
        </div>
        {inboxResult && (
          <div className={`p-3 rounded-lg border text-sm ${inboxResult.mode === "applied" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-secondary/40 border-border"}`}
               data-testid="backfill-inbox-result">
            <div className="font-medium mb-1">
              {inboxResult.mode === "applied" ? "✅ Toegepast" : "🔍 Preview"} — samenvatting:
            </div>
            <ul className="text-xs space-y-0.5 ml-4 list-disc">
              <li>Facturen: <strong>{inboxResult.facturen || 0}</strong></li>
              <li>Creditfacturen: <strong>{inboxResult.creditfacturen || 0}</strong></li>
              <li>Inkooporders (incl. doorzet): <strong>{inboxResult.inkooporders || 0}</strong></li>
              <li>Pakbonnen: <strong>{inboxResult.pakbonnen || 0}</strong></li>
              <li className="text-muted-foreground">Overgeslagen (al aanwezig): {inboxResult.skipped || 0}</li>
            </ul>
          </div>
        )}
      </div>

      {/* Card: forward-test — diagnostiek voor doorstuur-instellingen */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="forward-test-card">
        <div>
          <h2 className="font-display text-2xl font-medium">Doorsturen testen</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Test of e-mails die op een van je mailboxen binnenkomen correct worden doorgestuurd naar het bij die mailbox ingestelde <code>forward_naar</code>-adres (bijv. je persoonlijke Spark/Gmail). Handig als je vermoedt dat forwarding niet werkt.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-muted-foreground">Mailbox-adres (exact zoals bij Instellingen → E-mail → Afzenders staat)</label>
            <input type="email" value={fwMailbox} onChange={(e) => setFwMailbox(e.target.value)}
              placeholder="Inkoop@admin.nextproduct.nl"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm"
              data-testid="forward-test-mailbox" />
          </div>
          <Button onClick={forwardTest} disabled={fwBusy} data-testid="forward-test-btn">
            {fwBusy ? "Bezig…" : "Verstuur testmail"}
          </Button>
        </div>
        {fwResult && (
          <div className={`p-3 rounded-lg border text-sm ${fwResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
               data-testid="forward-test-result">
            {fwResult.ok ? (
              <>
                <div className="font-medium">✅ Test verstuurd van {fwResult.mailbox} → {fwResult.forward_naar}</div>
                {fwResult.api_key_present === false && (
                  <div className="text-xs mt-1 text-amber-800">⚠️ Deze mailbox heeft geen eigen Resend API-key ingesteld — er wordt teruggevallen op de globale key.</div>
                )}
                <div className="text-xs mt-1">{fwResult.hint}</div>
              </>
            ) : (
              <div>❌ {fwResult.reason || "Onbekende fout"}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
